/**
 * OCR client for the courier onboarding flow. Calls the CourierConnect
 * backend `POST /ocr/extract` endpoint which routes the uploaded file to
 * Google Document AI (driver license, owner-id, osek, bank confirmation)
 * and returns a unified `{ fields, raw }` shape that the documents step
 * uses to prefill subsequent form sections.
 */

import { apiClient } from 'shared/lib/apiClient';
import type { CourierBankAccountInfo } from 'features/courier-onboarding/types';
import { resolveIsraeliBank } from 'features/courier-onboarding/utils/bankCodeMapper';

export type ExtractDocType =
  | 'driverLicense'
  | 'ownerIdCard'
  | 'osekCertificate'
  | 'bankConfirmation';

export interface ExtractedFields {
  ownerId?: string | null;
  birthDate?: string | null;
  taxId?: string | null;
  ownerName?: string | null;
  officialBusinessName?: string | null;
  bankCode?: string | null;
  bankBranchNumber?: string | null;
  bankAccountNumber?: string | null;
  bankAccountOwnerId?: string | null;
}

export interface ExtractDocumentResponse {
  docType: ExtractDocType;
  fields: ExtractedFields;
  raw: Record<string, unknown>;
}

export interface ExtractionResult {
  ok: boolean;
  fields: ExtractedFields;
  bank?: Partial<CourierBankAccountInfo>;
  raw?: Record<string, unknown>;
  /**
   * Normalized (digits-only) 9-digit ת"ז extracted from this specific
   * document. For `bankConfirmation` this is sourced from
   * `raw.bank.data.account_holder_id`.
   */
  extractedIdNumber?: string;
  error?: string;
}

const isNonEmpty = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0;

const toDigits = (v: unknown): string | undefined => {
  if (v === null || v === undefined) return undefined;
  const digits = String(v).replace(/\D/g, '');
  return digits.length > 0 ? digits : undefined;
};

const getRawSection = (
  raw: Record<string, unknown> | undefined,
  key: string,
): Record<string, unknown> | undefined => {
  const section = raw?.[key];
  if (section && typeof section === 'object') {
    const data = (section as Record<string, unknown>).data;
    if (data && typeof data === 'object') return data as Record<string, unknown>;
  }
  return undefined;
};

const extractIdFromRaw = (
  docType: ExtractDocType,
  raw: Record<string, unknown> | undefined,
): string | undefined => {
  switch (docType) {
    case 'ownerIdCard': {
      const data = getRawSection(raw, 'customOwnerId');
      return toDigits(data?.owner_id);
    }
    case 'driverLicense': {
      const owner = getRawSection(raw, 'customOwnerId');
      if (owner?.owner_id) return toDigits(owner.owner_id);
      const docAi = getRawSection(raw, 'documentAi');
      return toDigits(docAi?.license_identity_number);
    }
    case 'osekCertificate': {
      const data = getRawSection(raw, 'osek');
      return toDigits(data?.dealer_number);
    }
    case 'bankConfirmation': {
      const data = getRawSection(raw, 'bank');
      return toDigits(data?.account_holder_id);
    }
    default:
      return undefined;
  }
};

/**
 * Map the backend's flat `fields` payload to a `CourierBankAccountInfo` patch
 * containing only the bank fields that were actually extracted. A known bank
 * code or name alias (including One Zero) is stored as the canonical numeric
 * code Monday expects, plus the Hebrew display name.
 */
const mapBankFields = (
  fields: ExtractedFields,
): Partial<CourierBankAccountInfo> | undefined => {
  const bank: Partial<CourierBankAccountInfo> = {};
  // Strip formatting (e.g. Bank Leumi prints account numbers as "27574/31"
  // which joins to "2757431" per the certificate's IBAN). The form schema
  // accepts digits-only, so we normalize here at the OCR→form boundary.
  if (isNonEmpty(fields.bankBranchNumber)) {
    const d = toDigits(fields.bankBranchNumber);
    if (d) bank.bankBranchNumber = d;
  }
  if (isNonEmpty(fields.bankAccountNumber)) {
    const d = toDigits(fields.bankAccountNumber);
    if (d) bank.bankAccountNumber = d;
  }
  if (isNonEmpty(fields.bankAccountOwnerId)) {
    const d = toDigits(fields.bankAccountOwnerId);
    if (d) bank.bankAccountOwnerId = d;
  }
  if (isNonEmpty(fields.bankCode)) {
    // Resolve the raw OCR value first. One Zero confirmations often print
    // "One Zero" or "וואן זירו" instead of the digits 18, and a leading zero
    // (`018`) must still become the canonical code Monday stores.
    const matched = resolveIsraeliBank(fields.bankCode);
    if (matched) {
      bank.bankCode = matched.code;
      bank.bankName = matched.name;
    } else {
      const code = toDigits(fields.bankCode);
      if (code) bank.bankCode = code;
    }
  }
  return Object.keys(bank).length > 0 ? bank : undefined;
};

export const extractDocument = async (params: {
  docType: ExtractDocType;
  url: string;
}): Promise<ExtractionResult> => {
  try {
    const res = await apiClient.post<ExtractDocumentResponse>(
      '/api/ocr/extract',
      {
        docType: params.docType,
        url: params.url,
      },
      { timeout: 60_000 },
    );
    const fields = res.data.fields ?? {};
    const extractedIdNumber = extractIdFromRaw(params.docType, res.data.raw);
    const bank =
      params.docType === 'bankConfirmation' ? mapBankFields(fields) : undefined;
    return {
      ok: true,
      fields,
      bank,
      raw: res.data.raw,
      extractedIdNumber,
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Unknown OCR extraction error';
    // eslint-disable-next-line no-console
    console.warn('[HAAT CourierConnect] OCR extraction failed', err);
    return { ok: false, fields: {}, error: message };
  }
};
